import { useMemo, useState } from "react";
import { Loader2, Timer } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useJornadaAgora, usePedirHoraExtra, usePessoasParaExtra } from "@/hooks/useJornada";

// Supervisor e gerente PEDEM hora extra ou sábado; quem aprova é a gestora do
// RH ou o master (Douglas 21/10/2026). Pedido do próprio RH já nasce aprovado.

function hojeSaoPaulo() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo" }).format(new Date());
}

export function PedirHoraExtraInline() {
  const { user } = useAuth();
  const { data: agora } = useJornadaAgora(Boolean(user));
  const [aberto, setAberto] = useState(false);
  const pessoas = usePessoasParaExtra(aberto);
  const pedir = usePedirHoraExtra();
  const [marcadas, setMarcadas] = useState<string[]>([]);
  const [dia, setDia] = useState(hojeSaoPaulo());
  const [inicio, setInicio] = useState("18:15");
  const [fim, setFim] = useState("19:30");
  const [motivo, setMotivo] = useState("");
  const lista = useMemo(() => pessoas.data ?? [], [pessoas.data]);

  if (!agora?.pode_pedir_extra) return null;

  const enviar = async () => {
    if (!marcadas.length) return toast.error("Marque quem vai trabalhar.");
    if (fim <= inicio) return toast.error("O fim tem que ser depois do início.");
    if (motivo.trim().length < 3) return toast.error("Escreva o motivo.");
    try {
      const r = await pedir.mutateAsync({ usuario_ids: marcadas, dia, inicio, fim, motivo: motivo.trim() });
      toast.success(r.status === "aprovada" ? "Liberação registrada e já valendo." : "Pedido enviado. O RH aprova ou recusa na aba Jornada.");
      setAberto(false);
      setMarcadas([]);
      setMotivo("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível enviar o pedido.");
    }
  };

  return (
    <>
      <button type="button" onClick={() => setAberto(true)} title="Pedir hora extra ou sábado para a sua equipe"
        className="inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-[11px] font-medium text-foreground/80 transition-colors hover:bg-accent/60 hover:text-foreground">
        <Timer className="h-3.5 w-3.5 text-primary" />
        <span className="hidden xl:inline text-muted-foreground">Hora extra</span>
      </button>
      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Pedir hora extra ou sábado</DialogTitle>
            <DialogDescription>O pedido vai para a gestora do RH aprovar. Aprovado, o CRM abre para essas pessoas no dia e no horário pedidos.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Quem</Label>
              <div className="mt-1 max-h-48 space-y-1 overflow-y-auto rounded-md border border-border p-2">
                {pessoas.isLoading ? <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  : lista.length === 0 ? <p className="text-xs text-muted-foreground">Ninguém da sua equipe trava pela jornada.</p>
                  : lista.map((p) => (
                    <label key={p.id} className="flex cursor-pointer items-center gap-2 text-sm">
                      <Checkbox checked={marcadas.includes(p.id)} onCheckedChange={(v) => setMarcadas((m) => (v ? [...m, p.id] : m.filter((id) => id !== p.id)))} />
                      <span className="truncate">{p.nome}</span>
                      <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">{p.equipe_nome}</span>
                    </label>
                  ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs text-muted-foreground">Dia</Label><Input type="date" value={dia} min={hojeSaoPaulo()} onChange={(e) => setDia(e.target.value)} className="mt-1 h-8 text-sm" /></div>
              <div><Label className="text-xs text-muted-foreground">De</Label><Input type="time" value={inicio} onChange={(e) => setInicio(e.target.value)} className="mt-1 h-8 text-sm" /></div>
              <div><Label className="text-xs text-muted-foreground">Até</Label><Input type="time" value={fim} onChange={(e) => setFim(e.target.value)} className="mt-1 h-8 text-sm" /></div>
            </div>
            <div><Label className="text-xs text-muted-foreground">Motivo</Label><Textarea value={motivo} onChange={(e) => setMotivo(e.target.value.slice(0, 500))} rows={2} placeholder="Fechamento do mês" className="mt-1 text-sm" /></div>
            <Button className="w-full" size="sm" disabled={pedir.isPending} onClick={enviar}>
              {pedir.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Enviar para o RH aprovar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
